import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import type { InvestorListDTO, InvoiceTransactionDraftDTO } from '@inversor-hub/types';
import type useSnackbar from '@/hooks/useSnackbar';
import type { useAnalytics } from '@/hooks/useAnalytics';
import { AiChip } from './shared';
import { formatReceived } from './utils';
import { DocumentViewer } from './DocumentViewer';
import { ReviewDraftForm } from './ReviewDraftForm';
import { useInvoiceDraftReview } from './useInvoiceDraftReview';

interface ReviewInvoiceDraftDialogProps {
  draft: InvoiceTransactionDraftDTO;
  investments?: InvestorListDTO[];
  onConfirmed: (investmentName: string | null) => void;
  onDiscarded: () => void;
  onClose: () => void;
  trackEvent: ReturnType<typeof useAnalytics>['trackEvent'];
  show: ReturnType<typeof useSnackbar>['show'];
}

export const ReviewInvoiceDraftDialog = ({
  draft,
  investments,
  onConfirmed,
  onDiscarded,
  onClose,
  trackEvent,
  show,
}: ReviewInvoiceDraftDialogProps) => {
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const review = useInvoiceDraftReview({
    draft,
    investments,
    onConfirmed,
    onDiscarded,
    trackEvent,
    show,
  });
  const { form, lowUnresolved, canConfirm, isBusy } = review.state;

  return (
    <Dialog
      open
      onClose={isBusy ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          width: '100%',
          maxWidth: 920,
          m: 2,
          maxHeight: '92vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <ReviewDialogHeader draft={draft} onClose={onClose} isBusy={isBusy} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        <DocumentPane draft={draft} />

        <Box sx={{ px: 3.25, py: 3 }}>
          <ReviewGuidance lowUnresolved={lowUnresolved} />
          {draft.duplicateWarning && <DuplicateWarning />}
          <ReviewDraftForm draft={draft} investments={investments} review={review} />
        </Box>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.75}
        sx={{ px: 3.25, py: 2, borderTop: 1, borderColor: 'grey.100' }}
      >
        <Button
          color="inherit"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => setConfirmDiscardOpen(true)}
          disabled={isBusy}
          sx={{ color: 'text.secondary', fontWeight: 600 }}
        >
          Descartar
        </Button>
        <Stack direction="row" alignItems="center" spacing={1.75}>
          {!form.investmentId && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.6}
              sx={{ color: (theme) => theme.palette.tokens.semantic.warningText }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: 12.5 }}>Asigna una inversión</Typography>
            </Stack>
          )}
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={review.actions.confirm}
            disabled={!canConfirm || isBusy}
          >
            Confirmar y guardar
          </Button>
        </Stack>
      </Stack>

      <DiscardDraftConfirmationDialog
        open={confirmDiscardOpen}
        isBusy={isBusy}
        onCancel={() => setConfirmDiscardOpen(false)}
        onConfirm={() => {
          setConfirmDiscardOpen(false);
          void review.actions.discard();
        }}
      />
    </Dialog>
  );
};

interface ReviewDialogHeaderProps {
  draft: InvoiceTransactionDraftDTO;
  onClose: () => void;
  isBusy: boolean;
}

const ReviewDialogHeader = ({ draft, onClose, isBusy }: ReviewDialogHeaderProps) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ px: 3.25, py: 2.5, borderBottom: 1, borderColor: 'grey.100' }}
  >
    <Stack direction="row" alignItems="center" spacing={1.4}>
      <IconButton
        onClick={onClose}
        disabled={isBusy}
        aria-label="Volver"
        sx={{
          bgcolor: 'grey.50',
          border: 1,
          borderColor: 'grey.100',
          borderRadius: '9px',
          width: 34,
          height: 34,
          color: 'text.secondary',
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Box>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary' }}>
          Revisar borrador
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.1 }}>
          {draft.source === 'INBOUND_EMAIL' ? (
            <MailOutlineIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
          ) : (
            <AttachFileIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
          )}
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            {draft.source === 'INBOUND_EMAIL'
              ? 'Recibida por email'
              : (draft.document?.fileName ?? 'Subida manual')}{' '}
            · {formatReceived(draft)}
          </Typography>
        </Stack>
      </Box>
    </Stack>
    <AiChip />
  </Stack>
);

const DocumentPane = ({ draft }: { draft: InvoiceTransactionDraftDTO }) => (
  <Box sx={{ bgcolor: 'grey.100' }}>
    <Box
      sx={{
        position: { md: 'sticky' },
        top: 0,
        px: 2.75,
        py: 3.25,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.9} sx={{ mb: 1.75 }}>
        <DescriptionOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 600,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            whiteSpace: 'nowrap',
          }}
        >
          Documento original
        </Typography>
      </Stack>
      <DocumentViewer draft={draft} />
    </Box>
  </Box>
);

const ReviewGuidance = ({ lowUnresolved }: { lowUnresolved: boolean }) => (
  <Stack
    direction="row"
    spacing={1.25}
    alignItems="flex-start"
    sx={{
      bgcolor: lowUnresolved
        ? (theme) => theme.palette.tokens.semantic.warningSurface
        : 'primary.50',
      border: 1,
      borderColor: lowUnresolved
        ? (theme) => theme.palette.tokens.semantic.warningBorder
        : 'primary.100',
      borderRadius: '11px',
      px: 1.75,
      py: 1.5,
      mb: 2.25,
    }}
  >
    {lowUnresolved ? (
      <WarningAmberIcon
        sx={{
          fontSize: 18,
          color: (theme) => theme.palette.tokens.semantic.warningText,
          flexShrink: 0,
          mt: 0.1,
        }}
      />
    ) : (
      <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0, mt: 0.1 }} />
    )}
    <Typography sx={{ fontSize: 13, color: 'text.primary', lineHeight: 1.5 }}>
      {lowUnresolved ? (
        <>
          La IA no está segura de los campos marcados en{' '}
          <Box
            component="strong"
            sx={{ color: (theme) => theme.palette.tokens.semantic.warningText }}
          >
            ámbar
          </Box>
          . Revísalos antes de guardar.
        </>
      ) : (
        'La IA ha rellenado el borrador. Revisa que todo sea correcto y confirma.'
      )}
    </Typography>
  </Stack>
);

const DuplicateWarning = () => (
  <Stack
    direction="row"
    spacing={1.25}
    alignItems="center"
    sx={{
      bgcolor: (theme) => theme.palette.tokens.semantic.warningSurface,
      border: 1,
      borderColor: (theme) => theme.palette.tokens.semantic.warningBorder,
      borderRadius: '11px',
      px: 1.75,
      py: 1.25,
      mb: 2.25,
    }}
  >
    <ErrorOutlineIcon
      sx={{ fontSize: 17, color: (theme) => theme.palette.tokens.semantic.warningText }}
    />
    <Typography
      sx={{ fontSize: 12.5, color: (theme) => theme.palette.tokens.semantic.warningText }}
    >
      Posible duplicado de una transacción ya registrada.
    </Typography>
  </Stack>
);

interface DiscardDraftConfirmationDialogProps {
  open: boolean;
  isBusy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const DiscardDraftConfirmationDialog = ({
  open,
  isBusy,
  onCancel,
  onConfirm,
}: DiscardDraftConfirmationDialogProps) => (
  <Dialog
    open={open}
    onClose={isBusy ? undefined : onCancel}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { borderRadius: '16px' } }}
  >
    <DialogContent sx={{ px: 3, pt: 3, pb: 1.5 }}>
      <Stack spacing={1.75}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            bgcolor: (theme) => theme.palette.tokens.semantic.warningSurface,
            color: (theme) => theme.palette.tokens.semantic.warningText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 23 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary' }}>
            Descartar borrador
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.75, lineHeight: 1.55 }}>
            Esta factura saldrá de la bandeja de revisión y no se creará ninguna transacción. Si la
            necesitas más adelante, tendrás que volver a subirla o reenviarla.
          </Typography>
        </Box>
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
      <Button color="inherit" onClick={onCancel} disabled={isBusy}>
        Cancelar
      </Button>
      <Button
        color="error"
        variant="contained"
        startIcon={<DeleteOutlineIcon />}
        onClick={onConfirm}
        disabled={isBusy}
      >
        Descartar
      </Button>
    </DialogActions>
  </Dialog>
);
